/**
 * BrandedLoader — centered Olytic 3-arc ring with a subtle pulse.
 *
 * Round 10 Item 63 (initial 3-arc shape) → Nitpick batch 4 (2026-06-25)
 * mistakenly refreshed to coin → Phase 1.4 (2026-06-29) restored to
 * 3-arc ring per Ean ("The Olytic circle is the icon of our company,
 * the url favicon, etc..."). The coin glyph is the TOKEN / CREDIT icon
 * (Block 3 IconCoin sweep), NOT the brand mark. Replaces the generic
 * grey spinner for large-area loading states (app boot, route Suspense
 * fallback, full-page "Loading workspace…"). The brand mark mirrors
 * /public/olytic-mark.png — the same asset the rest of the app renders.
 *
 * Used directly by <Loading /> (which keeps its existing API but now
 * paints the branded visual). Also exported as a standalone primitive
 * for callsites that want the explicit name.
 *
 * Sizes:
 *   - 'md'  → 44px mark (default; inline-card / non-full-page surface)
 *   - 'lg'  → 64px mark + 60vh min-height (full-page app boot)
 *
 * Inline variant (`inline`) sits next to text — used inside <Loading
 * inline /> for buttons / status lines. Smaller mark (16px), no
 * column-stack layout.
 *
 * Pulse animation (opacity 0.65 → 1 → 0.65 over 1.4s) is defined in
 * app.css `@keyframes olytic-pulse` and respects prefers-reduced-
 * motion via the global override at the end of app.css.
 */

interface BrandedLoaderProps {
  message?: string;
  size?: 'md' | 'lg';
  inline?: boolean;
}

export function BrandedLoader({ message, size = 'md', inline = false }: BrandedLoaderProps) {
  if (inline) {
    return (
      <span className="branded-loader branded-loader--inline" role="status" aria-live="polite">
        <OlyticMark size={16} />
        {message !== undefined && message.length > 0 && (
          <span className="branded-loader-message">{message}</span>
        )}
      </span>
    );
  }

  const markPx = size === 'lg' ? 64 : 44;
  const wrapClasses = ['branded-loader'];
  if (size === 'lg') wrapClasses.push('branded-loader--lg');

  return (
    <div className={wrapClasses.join(' ')} role="status" aria-live="polite">
      <OlyticMark size={markPx} />
      {message !== undefined && message.length > 0 && (
        <div className="branded-loader-message">{message}</div>
      )}
    </div>
  );
}

/**
 * OlyticMark — Olytic brand mark (2026-09 refresh, matches olytic.com).
 *
 * Loads /public/olytic-mark.svg (static ink planetary mark). The loader
 * appears pre-session on a fixed light background, so the mark stays in
 * Olytic identity colors regardless of per-workspace brand overrides
 * (Push 77 preserved — baked Olytic mark identity on the pre-session
 * boot path). The pulse animation is driven by the .branded-loader-mark
 * class (opacity 0.65→1→0.65 per @keyframes olytic-pulse in app.css),
 * which applies to the <img> the same way it did to the previous inline
 * SVG.
 */
function OlyticMark({ size }: { size: number }) {
  return (
    <img
      className="branded-loader-mark"
      src="/olytic-mark.svg"
      width={size}
      height={size}
      alt="Olytic"
    />
  );
}
